import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { FaEdit, FaTrash, FaPlus } from "react-icons/fa";
import "./BlogManagement.css";

const BlogManagement = () => {
  const [blogs, setBlogs] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    category: "",
    author: "",
    tags: "",
    readTime: "",
    image: null,
  });

  const categories = [
    "Health",
    "Medicine",
    "Lifestyle",
    "Wellness",
    "Medical Research",
    "Healthcare",
  ];

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const response = await axios.get("https://api.swasthmitra.com/api/blogs");
      setBlogs(response.data);
    } catch (error) {
      console.error("Error fetching blogs:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      image: e.target.files[0],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formDataToSend = new FormData();
    Object.keys(formData).forEach((key) => {
      if (key === "image") {
        if (formData.image) {
          formDataToSend.append("image", formData.image);
        }
      } else {
        formDataToSend.append(key, formData[key]);
      }
    });

    try {
      if (showEditForm) {
        await axios.put(
          `https://api.swasthmitra.com/api/blogs/${selectedBlog._id}`,
          formDataToSend
        );
      } else {
        await axios.post(
          "https://api.swasthmitra.com/api/blogs/create",
          formDataToSend
        );
      }
      fetchBlogs();
      resetForm();
    } catch (error) {
      console.error("Error saving blog:", error);
    }
  };

  const handleEdit = (blog) => {
    setSelectedBlog(blog);
    setFormData({
      title: blog.title,
      content: blog.content,
      category: blog.category,
      author: blog.author,
      tags: blog.tags.join(","),
      readTime: blog.readTime,
      image: null,
    });
    setShowEditForm(true);
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this blog post?")) {
      try {
        await axios.delete(`https://api.swasthmitra.com/api/blogs/${id}`);
        fetchBlogs();
      } catch (error) {
        console.error("Error deleting blog:", error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      content: "",
      category: "",
      author: "",
      tags: "",
      readTime: "",
      image: null,
    });
    setShowAddForm(false);
    setShowEditForm(false);
    setSelectedBlog(null);
  };

  return (
    <div className="blog-management">
      <div className="blog-management-header">
        <h2>{showEditForm ? "Edit Blog Post" : "Blog Management"}</h2>
        {!showAddForm && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="add-blog-btn"
            onClick={() => setShowAddForm(true)}
          >
            <FaPlus /> Add New Blog
          </motion.button>
        )}
      </div>

      {showAddForm ? (
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="blog-form"
          onSubmit={handleSubmit}
        >
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Content</label>
            <textarea
              name="content"
              value={formData.content}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Category</label>
            <select
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Category</option>
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Author</label>
            <input
              type="text"
              name="author"
              value={formData.author}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Tags (comma-separated)</label>
            <input
              type="text"
              name="tags"
              value={formData.tags}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <label>Read Time (minutes)</label>
            <input
              type="number"
              name="readTime"
              value={formData.readTime}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              required={!showEditForm}
            />
          </div>

          <div className="form-buttons">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="submit"
              className="submit-btn"
            >
              {showEditForm ? "Update Blog" : "Create Blog"}
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="button"
              className="cancel-btn"
              onClick={resetForm}
            >
              Cancel
            </motion.button>
          </div>
        </motion.form>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="blogs-list"
        >
          {blogs.map((blog) => (
            <motion.div
              key={blog._id}
              className="blog-item"
              whileHover={{ scale: 1.01 }}
            >
              <div className="blog-item-content">
                <img
                  src={blog.imageUrl}
                  alt={blog.title}
                  className="blog-image"
                />
                <div className="blog-details">
                  <h3>{blog.title}</h3>
                  <p className="blog-category">{blog.category}</p>
                  <p className="blog-author">By {blog.author}</p>
                </div>
              </div>
              <div className="blog-actions">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => handleEdit(blog)}
                >
                  <FaEdit />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => handleDelete(blog._id)}
                >
                  <FaTrash />
                </motion.button>
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}
    </div>
  );
};

export default BlogManagement;
